<template>
  <component
    :is="withdrawComponent"
    :shared="shared"
    :common="commonContext"
    @toggle-mode="displayCryptoChange"
  />
</template>

<script>
import { paymentList, withdrawalWallet, getCoinlist } from '@/api/payment'
import { info } from '@/api/user'
import { page } from '@/api/common'
import { showNotice, setErrorMessage, getWalletImagePath } from '@/utils/tool'
import { mapGetters } from 'vuex'
import themeMixin from '@/mixins/themeMixin'

import WithdrawDefaultForm from '@/views/withdraw/WithdrawDefaultForm'
import WithdrawCryptoExchangeForm from '@/views/withdraw/WithdrawCryptoExchangeForm'

export default {
  components: {
    WithdrawDefaultForm,
    WithdrawCryptoExchangeForm
  },
  mixins: [themeMixin],
  data() {
    return {
      shared: {
        wallets: [],
        mainWallet: null,
        coinList: [],
        paymentFeeData: [],
        selectedWallet: null,
        selectedWalletKey: null,
        selectedCoin: null,
        selectedChain: null,
        chainList: [],
        isCryptoSelect: true,
        agree: false,
        agreeTip: '',
        postForm: {
          wallet_type: 'main',
          amount: '',
          withdrawal_pass: null,
          opt_code: null,
          member_bank_id: this.$t('Bao.select_bankcard'),
          address: null
        }
      }
    }
  },
  computed: {
    ...mapGetters(['memberInfo', 'brand', 'brandInfo', 'bonusGold', 'user', 'permissionInfo', 'app', 'isOfflineAgentMember']),
    isCryptoExchange() {
      // 線下代理不可錢包互轉，即使直接以網址進入也強制回到一般出金
      return !this.isOfflineAgentMember && this.$route.query.mode === 'crypto-exchange'
    },
    mainWalletCurrency() {
      if (this.isTheme('theme_blue')) return 'UBD'
      if (this.isTheme('theme_purple')) return 'JRA'
      return this.brandInfo.currency || 'JPY'
    },
    withdrawComponent() {
      return this.isCryptoExchange ? 'WithdrawCryptoExchangeForm' : 'WithdrawDefaultForm'
    },
    google_auth_totp_status() {
      return this.user.authStatus && this.permissionInfo.enable_google_totp
    },
    first_deposit_at() {
      return this.memberInfo.first_deposit_at
    },
    commonContext() {
      return {
        app: this.app,
        brand: this.brand,
        brandInfo: this.brandInfo,
        bonusGold: this.bonusGold,
        first_deposit_at: this.first_deposit_at,
        google_auth_totp_status: this.google_auth_totp_status,
        memberInfo: this.memberInfo,
        permissionInfo: this.permissionInfo,
        user: this.user,
        buildMainWallet: this.buildMainWallet,
        getWalletByKey: this.getWalletByKey,
        getWalletImagePath,
        handleWalletInitError: this.handleWalletInitError
      }
    }
  },
  created() {
    // 提款密碼設定已移到安全中心
    if (this.memberInfo.member_type === 3 && this.memberInfo.withdrawal_pass === '') {
      this.$router.push('/user/verify')
    }

    // 線下代理不可錢包互轉，清掉網址上的 crypto-exchange 參數
    if (this.isOfflineAgentMember && this.$route.query.mode === 'crypto-exchange') {
      this.$router.replace({
        query: {
          ...this.$route.query,
          mode: undefined
        }
      }).catch(() => {})
    }

    page({ type: 'withdraw_agreement' }).then(response => {
      this.shared.agreeTip = response.data.data.content
    })

    withdrawalWallet().then((response) => {
      const allowedTypes = (this.brandInfo.wallet_types || []).map(w => w.wallet_type)
      this.shared.wallets = response.data.data.filter(w => allowedTypes.includes(w.wallet_type))
      this.shared.mainWallet = this.buildMainWallet()
      const fiatOnly = ['main', 'bonus', 'commission']
      const hasCrypto = (this.brandInfo.wallet_types || []).some(w => !fiatOnly.includes(w.wallet_type))
      if (hasCrypto) this.loadCoinList()
    }).catch((error) => this.handleWalletInitError(error))

    if (this.first_deposit_at === 0) {
      info().then(response => {
        this.$store.dispatch('SetUserData', response.data.data)
      })
    }
  },
  methods: {
    displayCryptoChange() {
      this.resetSharedModeState()
      this.$router.replace({
        query: {
          ...this.$route.query,
          mode: this.isCryptoExchange ? undefined : 'crypto-exchange'
        }
      }).catch(() => {})
    },
    resetSharedModeState() {
      this.shared.selectedWallet = null
      this.shared.selectedWalletKey = null
      this.shared.selectedCoin = null
      this.shared.selectedChain = null
      this.shared.chainList = []
      this.shared.isCryptoSelect = true
      this.shared.agree = false
      this.shared.postForm = {
        wallet_type: 'main',
        amount: '',
        withdrawal_pass: null,
        opt_code: null,
        member_bank_id: this.$t('Bao.select_bankcard'),
        address: null
      }
      this.shared.mainWallet = this.buildMainWallet()
    },
    getWalletByKey(key) {
      const wallet = this.shared.wallets.find(w => w.key === key)
      if (!wallet) return {}
      const matchInfo = this.user.walletInfo.find(info => info.wallet_type === wallet.wallet_type)
      return {
        ...wallet,
        withdrawal_limit: matchInfo ? matchInfo.withdrawal_limit : null,
        turnover: matchInfo ? matchInfo.turnover : 0
      }
    },
    buildMainWallet() {
      const raw = this.shared.wallets.length > 0
        ? this.getWalletByKey('main')
        : this.app.selectedWallet
      const currency = this.mainWalletCurrency
      return {
        ...raw,
        currency,
        symbol: currency,
        wallet_type: 'main',
        key: 'main'
      }
    },
    loadCoinList() {
      this.loadPaymentFee()
      getCoinlist().then((response) => {
        const rawList = response.data.data.filter(item =>
          this.shared.wallets.some(wallet => wallet.key === item.symbol)
        )

        const walletInfo = this.user.walletInfo || []
        this.shared.coinList = rawList.map(item => {
          const matchedWallet = walletInfo.find(w => w.wallet_type === item.symbol)
          const matchedFilteredWallet = this.shared.wallets.find(w => w.wallet_type === item.symbol)
          const balance = matchedWallet?.balance
            ? Math.floor(parseFloat(matchedWallet.balance) * 1e6) / 1e6
            : 0
          return {
            ...item,
            balance: matchedWallet ? balance.toFixed(6) : '0',
            withdrawal_limit: matchedWallet?.withdrawal_limit ?? '0',
            turnover: matchedWallet?.turnover ?? '0',
            amount_limit: matchedFilteredWallet?.amount_limit ?? {}
          }
        })
      }).catch((error) => {
        console.error('loadCoinList 發生錯誤：', error)
        setErrorMessage(error.response, this.errors)
      })
    },
    loadPaymentFee() {
      paymentList({ type: ['withdrawal'] }).then((response) => {
        this.shared.paymentFeeData = response.data.data
      }).catch((error) => setErrorMessage(error.response, this.errors))
    },
    handleWalletInitError(error) {
      const result = error.response.data
      if (result.status === 403) {
        showNotice(result.msg, () => {
          this.$router.push('/')
        })
      } else {
        if (!(result.data.type === 'bank_card' && this.isTheme('theme_purple'))) {
          showNotice(result.msg, () => {}, true, false, '')
        }
        if (result.data.type === 'info') {
          // 未設定提款密碼時導到安全中心的密碼設定，其餘缺資料導到帳號設定
          // 後端已設定時回傳 'has set'（同 performKYCCheck 判斷），其餘視為未設定
          const path = this.memberInfo.withdrawal_pass !== 'has set' ? '/user/verify' : '/user/edit'
          this.$router.push({ path }).catch(() => {})
        } else if (result.data.type === 'bank_card' && !this.isTheme('theme_purple')) {
          this.$router.push({ path: '/user/bank/create' }).catch(() => {})
        }
      }
    }
  }
}
</script>
